'use client';

import { useEffect, useState } from 'react';
import { fetchPrivateResume } from '@/lib/private-resume';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import type { ResumeDrawerProps } from './resume-drawer';

type ResumeLoader = (signal: AbortSignal) => Promise<Blob>;
type PrivateResumeDrawerProps = Omit<ResumeDrawerProps, 'resumeUrl'> & (
  | { privateResumeUrl: string; loadResume?: never }
  | { privateResumeUrl?: never; loadResume: ResumeLoader }
);

export function PrivateResumeDrawer({ privateResumeUrl, loadResume, fileName, candidateName, title, children }: PrivateResumeDrawerProps) {
  const [open, setOpen] = useState(false);
  const source = privateResumeUrl ?? loadResume;
  const [loaded, setFiles] = useState<{ source: string | ResumeLoader; original: string; preview?: string } | null>(null);
  const files = loaded?.source === source ? loaded : null;
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const objectUrls: string[] = [];
    setFiles(null);
    setError(null);
    void (async () => {
      try {
        const original = loadResume ? await loadResume(controller.signal) : await fetchPrivateResume(privateResumeUrl, false, controller.signal);
        if (controller.signal.aborted) return;
        const originalUrl = URL.createObjectURL(original);
        objectUrls.push(originalUrl);
        if (original.type === 'application/pdf') {
          setFiles({ source, original: originalUrl, preview: originalUrl });
          return;
        }
        setFiles({ source, original: originalUrl });
        // Capability loaders supply the original only; never fall back to recruiter credentials.
        if (loadResume) return;
        try {
          const preview = await fetchPrivateResume(privateResumeUrl, true, controller.signal);
          if (controller.signal.aborted || preview.type !== 'application/pdf') return;
          const previewUrl = URL.createObjectURL(preview);
          objectUrls.push(previewUrl);
          setFiles({ source, original: originalUrl, preview: previewUrl });
        } catch { /* The original remains available while a derived preview is pending. */ }
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'This resume is unavailable.');
      }
    })();
    return () => { controller.abort(); objectUrls.forEach(url => URL.revokeObjectURL(url)); };
  }, [open, privateResumeUrl, loadResume, source]);

  return <Sheet open={open} onOpenChange={next => { setOpen(next); if (!next) setFiles(null); }}>
    <SheetTrigger asChild>{children || <Button variant="outline" size="sm"><FileText className="w-4 h-4 mr-2" />Resume</Button>}</SheetTrigger>
    <SheetContent side="right" aria-describedby={undefined} className="w-full sm:max-w-2xl p-0 flex flex-col">
      <SheetHeader className="px-6 py-4 border-b shrink-0">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-lg">{title || (candidateName ? `${candidateName}'s Resume` : 'Resume')}</SheetTitle>
          <div className="flex items-center gap-2">
            {files ? <>
              <Button variant="outline" size="sm" asChild><a href={files.original} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4 mr-1" />Open</a></Button>
              <Button variant="outline" size="sm" asChild><a href={files.original} download={fileName || 'resume'}><Download className="w-4 h-4 mr-1" />Download</a></Button>
            </> : <><Button variant="outline" size="sm" disabled>Open</Button><Button variant="outline" size="sm" disabled>Download</Button></>}
          </div>
        </div>
      </SheetHeader>
      <div className="flex-1 relative bg-muted/30 overflow-hidden">
        {!files && !error && <div role="status" className="flex items-center justify-center gap-3 p-6 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" />Loading resume…</div>}
        {error && <div role="alert" className="p-6 text-sm text-muted-foreground">{error}</div>}
        {files?.preview && <iframe src={`${files.preview}#view=FitH`} className="w-full h-full border-0" title="Resume Preview" />}
        {files && !files.preview && <div className="p-6 text-sm text-muted-foreground">Preview unavailable. You can open or download the original document.</div>}
      </div>
    </SheetContent>
  </Sheet>;
}
